// Multi-Pass QR-Erkennung auf ImageData.
//
// Strategie:
//   1. Native BarcodeDetector (Chromium/Edge/Android, blitzschnell)
//   2. jsQR (WASM-frei, robustes Fallback)
// Pro ImageData werden mehrere Varianten probiert: Original, Invertiert
// und – falls noch nichts gefunden wurde – vordefinierte Quadranten/
// Bereiche (typische QR-Positionen in Rechnungen: unten links, unten
// rechts, oben rechts).

import jsQR from 'jsqr';

import {
  cropImageData,
  invertImageData,
  type RenderedPage,
} from './pdfToImages';

type GlobalWithBarcode = typeof globalThis & {
  BarcodeDetector?: new (opts: { formats: string[] }) => {
    detect: (
      img: ImageBitmap | HTMLCanvasElement
    ) => Promise<Array<{ rawValue: string; format: string }>>;
  };
};

let _detector: InstanceType<
  NonNullable<GlobalWithBarcode['BarcodeDetector']>
> | null = null;
let _detectorReady = false;

async function getNativeDetector() {
  if (_detectorReady) return _detector;
  _detectorReady = true;
  const g = globalThis as GlobalWithBarcode;
  if (!g.BarcodeDetector) return null;
  try {
    _detector = new g.BarcodeDetector({ formats: ['qr_code'] });
  } catch {
    _detector = null;
  }
  return _detector;
}

async function tryBarcodeDetector(img: ImageData): Promise<string | null> {
  const det = await getNativeDetector();
  if (!det) return null;
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.putImageData(img, 0, 0);
  try {
    const results = await det.detect(canvas);
    if (results && results.length > 0) {
      return results[0].rawValue;
    }
  } catch {
    /* ignore */
  } finally {
    canvas.width = 0;
    canvas.height = 0;
  }
  return null;
}

function tryJsQr(img: ImageData): string | null {
  try {
    const result = jsQR(img.data, img.width, img.height, {
      inversionAttempts: 'attemptBoth',
    });
    return result?.data ?? null;
  } catch {
    return null;
  }
}

async function scanImage(img: ImageData): Promise<string | null> {
  const native = await tryBarcodeDetector(img);
  if (native) return native;
  const js = tryJsQr(img);
  if (js) return js;
  return null;
}

/** Versucht in einer einzelnen Seite einen QR-Code zu lesen. */
async function scanPage(page: RenderedPage): Promise<string | null> {
  // 1) Volles Bild
  const full = await scanImage(page.imageData);
  if (full) return full;

  // 2) Invertiert (helle QR auf dunklem Grund)
  const inverted = invertImageData(page.imageData);
  const fullInv = await scanImage(inverted);
  if (fullInv) return fullInv;

  // 3) Quadranten und typische Rechnungs-QR-Positionen
  const w = page.imageData.width;
  const h = page.imageData.height;
  const regions: Array<[number, number, number, number]> = [
    [0, h * 0.5, w * 0.5, h * 0.5], // unten links
    [w * 0.5, h * 0.5, w * 0.5, h * 0.5], // unten rechts
    [w * 0.5, 0, w * 0.5, h * 0.5], // oben rechts
    [0, 0, w * 0.5, h * 0.5], // oben links
    [w * 0.25, h * 0.6, w * 0.5, h * 0.4], // EPC-typischer Block unten Mitte
  ];
  for (const [x, y, rw, rh] of regions) {
    const cropped = cropImageData(page.imageData, x, y, rw, rh);
    const r = await scanImage(cropped);
    if (r) return r;
  }
  return null;
}

/** Scannt alle gerenderten Seiten und liefert den ersten Treffer.
 *  Reihenfolge: kleinere Skala zuerst (schneller), bei Misserfolg gross. */
export async function detectQrInPages(
  pages: RenderedPage[]
): Promise<string | null> {
  const sorted = [...pages].sort((a, b) => {
    if (a.pageNumber !== b.pageNumber) return a.pageNumber - b.pageNumber;
    return a.scale - b.scale;
  });
  for (const page of sorted) {
    const r = await scanPage(page);
    if (r) return r;
  }
  return null;
}
